import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { LayoutDashboard, SquareParking, ScanLine, Truck, IdCard, Receipt, Bell, Radio, Building2, ShieldCheck, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errorText } from "@/lib/api";
import { foApi } from "@/webapp/fleet-owners/api";
import SecurityCenter from "@/webapp/security/SecurityCenter";
import CommandCenter from "@/webapp/fleet-owners/tabs/CommandCenter";
import Parking from "@/webapp/fleet-owners/tabs/Parking";
import GateProof from "@/webapp/fleet-owners/tabs/GateProof";
import Vehicles from "@/webapp/fleet-owners/tabs/Vehicles";
import Drivers from "@/webapp/fleet-owners/tabs/Drivers";
import Billing from "@/webapp/fleet-owners/tabs/Billing";
import Alerts from "@/webapp/fleet-owners/tabs/Alerts";
import Connections from "@/webapp/fleet-owners/tabs/Connections";
import Company from "@/webapp/fleet-owners/tabs/Company";
import "@/webapp/fleet-owners/fleet.css";

const GROUPS = [
  { label: "Operate", items: [
    { id: "command", label: "Command Center", icon: LayoutDashboard, C: CommandCenter },
    { id: "parking", label: "Parking & Bookings", icon: SquareParking, C: Parking },
    { id: "gate", label: "Gate Proof", icon: ScanLine, C: GateProof },
  ] },
  { label: "Fleet", items: [
    { id: "vehicles", label: "Vehicles", icon: Truck, C: Vehicles },
    { id: "drivers", label: "Drivers", icon: IdCard, C: Drivers },
  ] },
  { label: "Money", items: [{ id: "billing", label: "Billing & Spend", icon: Receipt, C: Billing }] },
  { label: "System", items: [
    { id: "alerts", label: "Alerts", icon: Bell, C: Alerts },
    { id: "connections", label: "Telematics", icon: Radio, C: Connections },
    { id: "company", label: "Company", icon: Building2, C: Company },
    { id: "security", label: "Security", icon: ShieldCheck, C: SecurityCenter },
  ] },
];
const ITEMS = GROUPS.flatMap((g) => g.items);
const ROLES = ["fleet_owner", "fleet_manager", "admin"];

function SideNav({ active, go, alerts, prefix = "fo-nav" }) {
  return (
    <nav className="space-y-6" data-testid={`${prefix}-list`}>
      {GROUPS.map((g) => (
        <div key={g.label}>
          <p className="mb-2 px-3 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-white/55">{g.label}</p>
          {g.items.map(({ id, label, icon: Icon }) => (
            <button key={id} data-testid={`${prefix}-${id}`} onClick={() => go(id)}
              className={`group relative flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${active === id ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5 hover:text-white"}`}>
              {active === id && <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r bg-[#FF5722]" />}
              <Icon className={`h-4 w-4 ${active === id ? "text-[#FF5722]" : ""}`} strokeWidth={1.75} />
              <span className="flex-1 text-left">{label}</span>
              {id === "alerts" && alerts > 0 && <span className="rounded bg-[#FF5722] px-1.5 font-mono text-[10px] font-bold text-white" data-testid={`${prefix}-alerts-count`}>{alerts}</span>}
            </button>
          ))}
        </div>
      ))}
    </nav>
  );
}

function Brand() {
  return <span className="font-display text-lg font-bold tracking-tight text-white">FLEET<span className="text-[#FF5722]">PODS</span><span className="ml-2 align-middle font-mono text-[10px] font-medium tracking-[0.2em] text-white/40">FLEET</span></span>;
}

function TopBar({ data, user, logout, go, onMenu }) {
  const n = data?.kpis?.alerts_open || 0;
  return (
    <header className="fo-noprint sticky top-0 z-50 flex h-14 items-center gap-4 border-b border-slate-200 bg-white px-4 lg:px-8">
      <button data-testid="fo-menu-open" onClick={onMenu} className="rounded-md p-1.5 text-[#0A234A] hover:bg-slate-100 lg:hidden"><Menu className="h-5 w-5" /></button>
      <span className="truncate font-display text-sm font-semibold text-[#0A234A]" data-testid="fo-company-header">{data?.company || "Fleet"}</span>
      {data?.home_base && <span className="hidden font-mono text-xs text-slate-400 md:inline">· {data.home_base}</span>}
      <div className="ml-auto flex items-center gap-3 text-sm">
        <button data-testid="fo-bell" onClick={() => go("alerts")} className="relative rounded-md p-2 text-[#0A234A] transition-colors hover:bg-slate-100">
          <Bell className="h-4 w-4" />
          {n > 0 && <span className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-[#FF5722] px-1 text-center font-mono text-[10px] font-bold leading-[18px] text-white" data-testid="fo-bell-count">{n}</span>}
        </button>
        <span className="hidden font-mono text-xs text-slate-400 sm:inline" data-testid="fo-user-email">{user.email}</span>
        <button data-testid="fo-signout" onClick={logout} className="rounded-md px-2.5 py-1.5 font-semibold text-[#0A234A] transition-colors hover:bg-slate-100">Sign out</button>
      </div>
    </header>
  );
}

function MobileNav({ open, onClose, active, go, alerts }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] lg:hidden" data-testid="fo-mobile-nav">
      <button aria-label="Close menu" onClick={onClose} className="absolute inset-0 bg-[#0A234A]/40" />
      <aside className="fo-drawer relative h-full w-72 overflow-y-auto bg-[#0A234A] p-5">
        <div className="mb-8 flex items-center justify-between"><Brand /><button data-testid="fo-menu-close" onClick={onClose} className="text-white/70"><X className="h-5 w-5" /></button></div>
        <SideNav prefix="fo-mnav" active={active} go={(id) => { go(id); onClose(); }} alerts={alerts} />
      </aside>
    </div>
  );
}

export default function FleetOwnerConsole() {
  const { user, logout } = useAuth();
  const [active, setActive] = useState("command");
  const [menu, setMenu] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const authed = user && ROLES.includes(user.role);
  const load = useCallback(async () => {
    try { setData(await foApi.state()); setError(""); } catch (e) { setError(errorText(e, "Could not load fleet data.")); }
  }, []);
  useEffect(() => { if (authed) load(); }, [authed, load]);
  const go = (id) => { setActive(id); window.scrollTo({ top: 0 }); };

  if (user === undefined) return <div className="flex min-h-[100dvh] items-center justify-center text-sm font-semibold text-slate-400" data-testid="fo-boot">Loading…</div>;
  if (!authed) return <Navigate to="/dashboard" replace />;

  const Active = ITEMS.find((n) => n.id === active)?.C || CommandCenter;
  const alerts = data?.kpis?.alerts_open || 0;
  return (
    <div data-testid="fleetowner-console" className="fo-shell flex min-h-[100dvh] text-[#0A234A]">
      <aside className="fo-noprint sticky top-0 hidden h-[100dvh] w-64 flex-shrink-0 flex-col bg-[#0A234A] px-4 py-6 lg:flex">
        <div className="mb-10 px-3"><Brand /></div>
        <div className="flex-1 overflow-y-auto"><SideNav active={active} go={go} alerts={alerts} /></div>
        <p className="px-3 font-mono text-[10px] text-white/50">Live data only · no sample records</p>
      </aside>
      <MobileNav open={menu} onClose={() => setMenu(false)} active={active} go={go} alerts={alerts} />
      <div className="fo-grid-bg min-w-0 flex-1">
        <TopBar data={data} user={user} logout={logout} go={go} onMenu={() => setMenu(true)} />
        <main className="mx-auto max-w-[1400px] p-5 pb-16 sm:p-8 lg:p-10">
          {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-600" data-testid="fo-error">{error}</div>}
          {!data && !error && <div className="flex h-[60vh] items-center justify-center text-sm font-semibold text-slate-400" data-testid="fo-loading">Loading…</div>}
          {data && <div key={active}><Active data={data} reload={load} go={go} /></div>}
        </main>
      </div>
    </div>
  );
}
